<template>
    <div id="ai-tree-list-edit" :class="meta?.maxWidth ? meta.maxWidth : '' + ' h-full'">
        <component
            :is="componentName"
            ref="treeListRef"
            :args="args"
            :dataSource="displayList"
            :editing="editing"
            :locked-rows="lockedRows"
            :lookups-data="displayLookupData"
            :meta="treeListMeta"
            :preferencesState="state.prefState"
            :preselectedRows="preselectedRows"
            :show-grid-footer="props.showFooter"
            :to-remove-toolbar-items-names="toRemoveToolbarItemsNames"
            :toolbar-config="toolbarConfig"
            :toolbarShow="props.toolbarShow"
            :toolbarUserItems="toolbarUserItems"
            @changesSavedSuccessfully="emit('changesSavedSuccessfully')"
            @cutPutEnd="onCutPutEnd"
            @dragChange="onDragChange"
            @dragReorder="onDragReorder"
            @escapeDownClick="onEscapeDownClick"
            @treeListEditCoreMounted="treeListEditCoreMountedHandler"
            @userDataChanged="e => emit('userDataChanged', e)"
            @tree-list-focus-changed="handleFocusChanged"
            @save-click="onSaveClick"
            @delete-click="onDeleteClick"
            @cell-value-changed="onCellValueChanged"
            @user-settings-changed="onUserSettingsChanged"
            @user-settings-reset="onUserSettingsReset"
            @refresh-click="onRefreshClick"
            @cell-click="(viewId, event, key) => emit('cellClick', { viewId, event, key })"
            @toolbar-button-click="toolbarButtonClick"
            @save-settings="onSaveSettings"
        >
            <template #cellTemplate="{ data }">
                <div class="relative flex align-center">
                    <div
                        :class="
                            'min-h-[16px] overflow-hidden ' +
                            highLightMatches(data.key, data.column.dataField, 'highlight-cell') +
                            moveNumber(data)
                        "
                    >
                        {{ data.text }}
                    </div>
                    <div class="absolute right-0 top-[-4px]">
                        <div
                            v-if="isCellHasError(data)"
                            class="w-6 h-6 bg-red-600 text-white rounded-full text-lg flex justify-center items-center"
                            @click="event => onErrorSignClick(event, data)"
                        >
                            !
                        </div>
                    </div>
                    <div
                        v-if="showErrorMessage(data)"
                        class="absolute bg-red-500 text-white p-[5px] z-50 mt-[17px] w-full opacity-75 rounded-[7px] group-hover:opacity-100 transition duration-300 ease-in-out whitespace-pre-wrap"
                    >
                        {{ errorList[currentField] }}
                    </div>
                </div>
            </template>
            <template v-for="(_, name) in $slots" #[name]="{ data }">
                <slot :data="data" :name="name" locate-in-menu="auto"></slot>
            </template>
        </component>
    </div>
</template>
<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import {
    deleteUserDataChanges,
    deleteUserSettingsChanges,
    getUserDataChanges,
    getUserSettingsChanges,
    saveUserDataChanges,
} from '@/service/userdata-service';
import { commitDataChanges, getDataSource } from '@/service/meta-service';
import { dxUiNotify } from '@/utils/dx-helper';
import AiCustomConfirmDialog from '@/utils/ai-custom-confirm-dialog';
import { setTabReadyToSaveValue, tabState } from '@/layouts/main-layout/components/navigation-bars-data';
import { mainState } from '@/layouts/main-layout/main-layout-data';

const currentField = ref(null);
const timeoutId = ref();
const currentRow = ref();
const errorList = ref([]);
const componentName = ref('ai-tree-list-edit-core');
const emit = defineEmits([
    'userSettingsChanged',
    'treeListEditMounted',
    'userDataChanged',
    'cellClick',
    'toolbarButtonClick',
    'refreshEnd',
    'escapeDownClick',
    'treeListFocusChanged',
    'changesSavedSuccessfully',
    'insertSave',
    'dragChange',
    'dragReorder',
    'cutPutEnd',
]);
const isErrorMessageVisible = ref(false);
const props = defineProps({
    meta: {
        type: Object,
        default: () => {},
    },
    action: {
        type: Function,
        default: null,
    },
    args: {
        type: Object,
        default: () => {},
    },
    lockedRows: {
        type: Array,
        default: () => [],
    },
    toolbarShow: {
        type: Boolean,
        default: () => true,
    },
    toRemoveToolbarItemsNames: {
        type: Array,
        default: () => ['moveSave', 'moveTo', 'choice', 'edit'],
    },
    showFooter: {
        type: Boolean,
        default: true,
    },
    hasntRoute: {
        type: Boolean,
        default: () => false,
    },
    toolbarUserItems: {
        type: Array,
        default: () => [],
    },
    editing: {
        type: Object,
        default: () => {},
    },
    preselectedRows: {
        type: Array,
        default: () => [],
    },
});

const state = reactive({
    lookupDataSource: {},
    dataSource: [],
    prefState: true,
    selectedRowKeys: [],
    treeListChanges: [],
    userSettings: {},
});

const onErrorSignClick = (event, data) => {
    if (timeoutId.value) {
        clearTimeout(timeoutId.value);
    }
    event.stopPropagation();
    errorList.value = findMessage(data);
    currentField.value = data.column.dataField;
    currentRow.value = data.row.key;
    isErrorMessageVisible.value = true;
    timeoutId.value = setTimeout(() => {
        isErrorMessageVisible.value = false;
    }, 15000);
};

const toolbarButtonClick = e => {
    isErrorMessageVisible.value = false;
    emit('toolbarButtonClick', e);
};

const showErrorMessage = data => {
    return (
        isCellHasError(data) &&
        isErrorMessageVisible.value &&
        data.column.dataField === currentField.value &&
        data.row.key === currentRow.value
    );
};

const onEscapeDownClick = highlightedRowsElements => {
    emit('escapeDownClick', highlightedRowsElements);
};

const setCacheOrApiDataSource = async () => {
    if (props.hasntRoute) {
        await setDataSource();
        return;
    }

    if (tabState[mainState.routePath] && tabState[mainState.routePath]?.data) {
        await setDataSource(tabState[mainState.routePath]?.data);
    } else {
        const res = await setDataSource();
        tabState[mainState.routePath] ??= {};
        tabState[mainState.routePath].data = res.data;
    }
};

const setDataSource = async (cacheData, selectArgs) => {
    const { requiredArgs } = props.meta;
    const currentExpandedRowKeys = treeListInstance.value?.option('expandedRowKeys') || [];

    if (requiredArgs) {
        const hasAllRequiredArgs = requiredArgs.select.every(arg =>
            Object.prototype.hasOwnProperty.call(args.value.select, arg)
        );

        if (!hasAllRequiredArgs) {
            state.lookupDataSource = [];
            state.dataSource = [];
            return;
        }
    }

    let result;

    if (cacheData) {
        result = {
            result: true,
            data: cacheData,
        };
    } else {
        const finalArgs = selectArgs ? selectArgs : args.value.select;
        result = await getDataSource(props.meta.api.select, finalArgs);
    }
    // const { result, data } = await getDataSource(props.meta.api.select, args.value.select);

    state.lookupDataSource = getLookUpsDs(result.result && result.data ? result.data : []);
    state.dataSource = result.result && result.data ? result.data : [];
    treeListInstance.value?.option('expandedRowKeys', currentExpandedRowKeys);
    return result;
};

const fetchAndSetUserData = async args => {
    const { result, changes } = await getUserDataChanges(args);

    if (result && changes) {
        const { selectedRowKeys, treeListChanges, searchPanelValue, lookupData } = changes;

        state.selectedRowKeys = selectedRowKeys ? selectedRowKeys : [];
        state.treeListChanges = treeListChanges ? treeListChanges : [];

        if (searchPanelValue) treeListRef.value.setSearchPanelValue(searchPanelValue);

        if (lookupData) {
            Object.keys(state.lookupDataSource).forEach(key => {
                state.lookupDataSource[key] = [...state.lookupDataSource[key], ...lookupData];
            });
        }
    } else {
        state.selectedRowKeys = [];
        state.treeListChanges = [];
    }
};

const getLookupColumns = () => {
    const columns = [];
    for (const col of props.meta.columns) {
        if (col.editor) {
            const isLookupEditor = col.editor.type === 'ai-drop-down' || col.editor.type === 'ai-popup';
            if (isLookupEditor) {
                columns.push(col);
            }
        }
    }
    return columns;
};
const generateLookupDs = (col, ds) => {
    const lookupDs = [];
    for (const row of ds) {
        const lookupRow = {};
        lookupRow[col.dataField] = row[col.dataField];
        lookupRow[col.editor.text] = row[col.editor.text];
        lookupDs.push(lookupRow);
    }
    lookupDs.push({
        id_ro_grid_11_popup: 9,
        name_ro_11: 'hi',
    });
    return lookupDs;
};

const getLookUpsDs = dataSource => {
    const columns = getLookupColumns();
    const lookups = {};
    for (const col of columns) {
        lookups[col.dataField] = generateLookupDs(col, dataSource);
    }

    return lookups;
};

const onSaveSettings = () => {
    emit('save-settings');
};

const fetchAndSetUserSettings = async args => {
    const { result, settings } = await getUserSettingsChanges(args);

    if (result && settings) {
        state.userSettings = settings;
        setPrefState(settings);

        if (settings.filterRowValues) {
            treeListRef.value?.setColumnFiltersValues(settings.filterRowValues);
        }
    } else {
        state.userSettings = {};
        state.prefState = true;
    }
};

const setPrefState = settings => {
    if (!settings) {
        state.prefState = true;
        return;
    }
    switch (settings.preferencesState) {
        case false:
            state.prefState = false;
            break;
        case undefined:
        case null:
        case true:
        default:
            state.prefState = true;
            break;
    }
};

const treeListRef = ref();
const treeListInstance = ref();
const isUserDataLoaded = ref(false);
const systemErrorIds = ref([]);
const validationErrorsList = ref([]);
const highLightMatches = ref();
const tooltipConfigList = ref([]);
const displayList = computed(() => state.dataSource);
const displayLookupData = computed(() => state.lookupDataSource);
const lazyFocusKeyExpr = ref();
const args = computed(() => {
    const { keyExpr, parentIdExpr } = props.meta;
    const keyFields = {
        keyExpr,
        parentIdExpr,
    };

    if (props.args) {
        return {
            save: { ...props.args.save, ...keyFields },
            select: { ...props.args.select },
        };
    } else {
        return {
            save: { ...keyFields },
            select: {},
        };
    }
});

const treeListMeta = computed(() => {
    const updatedMeta = { ...props.meta };
    updatedMeta.columns.forEach(col => {
        if (col.dataType !== 'boolean') col.cellTemplate = 'cellTemplate';
    });

    const columnsWithIndex = updatedMeta.columns.filter(item => item.index);
    const maxIndex = columnsWithIndex.length > 0 ? Math.max(...columnsWithIndex.map(item => item.index)) : -1;
    const isDublicate = findDuplicates(columnsWithIndex);

    if (isDublicate) throw new Error('Ошибка: индексы в колонках должны иметь уникальное значение');

    if (columnsWithIndex.length === 0) {
        updatedMeta.columns = updatedMeta.columns.map((el, index) => ({
            ...el,
            index: index,
            visible: el.visible !== undefined ? el.visible : true,
        }));
    }

    if (maxIndex !== -1) {
        updatedMeta.columns = updatedMeta.columns.map((el, index) => {
            if (el.index === undefined) {
                return {
                    ...el,
                    index: maxIndex + index + 1,
                    visible: el.visible !== undefined ? el.visible : true,
                };
            } else {
                return el;
            }
        });
    }

    return updatedMeta;
});

const findDuplicates = columns => {
    const indexes = columns.length > 0 ? columns.map(el => el.index) : [];
    const uniqueIndexes = new Set();
    for (let i of indexes) {
        if (uniqueIndexes.has(i)) return true;
        uniqueIndexes.add(i);
    }

    return false;
};

const setFocusdRow = keyExprValue => {
    if (displayList.value.length) {
        _setFocusedRow(keyExprValue);
    } else {
        lazyFocusKeyExpr.value = keyExprValue;
    }
};

const _setFocusedRow = keyExprValue => {
    const neededRowIndex = treeListInstance.value.expandRow(keyExprValue);
    treeListInstance.value.option('focusedRowIndex', neededRowIndex);
    treeListInstance.value.option('focusedRowKey', keyExprValue);
};

onMounted(async () => {});

const treeListEditCoreMountedHandler = async () => {
    highLightMatches.value = treeListRef.value.highLightMatches;
    treeListInstance.value = treeListRef.value.treeListInstance;
    emit('mounted');
    treeListInstance.value?.on('contentReady', onContentReady);
    treeListInstance.value?.on('cellPrepared', () => {
        isErrorMessageVisible.value = false;
    });

    if (props.action) {
        props.action({
            instance: treeListInstance.value,
            meta: props.meta,
            saveUserDataChanges,
            setUserDataChanges,
            setDataSource,
            setFocusdRow,
            componentRef: treeListRef.value,
        });
    }

    isUserDataLoaded.value = false;

    // await setDataSource();
    await fetchAndSetUserData({ viewId: props.meta.viewId });
    await fetchAndSetUserSettings({ viewId: props.meta.viewId });

    const treeListState = treeListInstance.value.state();
    const updatedTreeListState = {
        ...treeListState,
        ...state.userSettings,
        selectedRowKeys: state.selectedRowKeys,
    };

    // treeListInstance.value.beginUpdate();
    treeListInstance.value.state(updatedTreeListState);
    // treeListInstance.value.endUpdate();

    treeListInstance.value.option('editing.changes', state.treeListChanges);

    isUserDataLoaded.value = true;

    // onUserSettingsReset();
    emit('treeListEditMounted');
};

const editing = computed(() => {
    const { rights } = props.meta;

    if (!rights) {
        return {
            enableDeleting: true,
            allowAdding: true,
            allowUpdating: true,
        };
    }

    let result;

    if (!rights.IDU) {
        result = {
            enableDeleting: false,
            allowAdding: false,
            allowUpdating: false,
        };
    } else {
        result = {
            enableDeleting: true,
            allowAdding: true,
            allowUpdating: true,
        };
    }

    if (Object.prototype.hasOwnProperty.call(rights, 'INS')) result.allowAdding = rights.INS;

    if (Object.prototype.hasOwnProperty.call(rights, 'UPD')) result.allowUpdating = rights.UPD;

    if (Object.prototype.hasOwnProperty.call(rights, 'DEL')) result.enableDeleting = rights.DEL;

    return result;
});

const isCellHasError = ({ column, row }) => {
    let result = false;
    validationErrorsList.value.forEach(({ item: { greyEminence }, field }) => {
        if (greyEminence === row.key && field === column.dataField) result = true;
    });
    return result;
};

const moveNumber = cellData => {
    const result = isCellHasError(cellData);
    if (result) {
        return 'pr-[30px]';
    }
    return '';
};

const findMessage = data => {
    const errorMessagesMap = {};

    tooltipConfigList.value.forEach(item => {
        const errors = tooltipConfigList.value.filter(error => error.key === data.key && error.field === item.field);
        if (errors.length > 0) {
            errorMessagesMap[item.field] = errors.map(err => err.message).join(' ');
        } else {
            errorMessagesMap[item.field] = '';
        }
    });

    return errorMessagesMap;
};

const onContentReady = () => {
    highlightErrors();

    systemErrorIds.value.forEach(id => {
        const index = treeListInstance.value.getRowIndexByKey(id);
        if (index > -1) {
            const element = treeListInstance.value.getRowElement(index)[0];
            element.classList.add('bg-red-500');
        }
    });
};

const highlightErrors = () => {
    validationErrorsList.value.forEach(error => {
        const rowIndex = treeListInstance.value.getRowIndexByKey(error.item.greyEminence);
        const cell = treeListInstance.value.getCellElement(rowIndex, error.field);

        if (cell) {
            cell.classList.add('dx-validator');
            cell.classList.add('dx-treelist-invalid');
            cell.classList.add('dx-treelist-validator');
            cell.classList.add('group');
        }
    });
};

const handleFocusChanged = async (row, viewId, RowIndex, component) => {
    emit('treeListFocusChanged', row, viewId, RowIndex, component);
};

const onSaveClick = async (userChanges, reorderedRows) => {
    systemErrorIds.value = [];
    validationErrorsList.value = [];
    tooltipConfigList.value = [];

    const commitDataChangesResult = await commitDataChanges(props.meta.api.save, userChanges, args.value.save);
    const { errors, validationErrors, insert } = commitDataChangesResult;
    emit('insertSave', insert);
    await processErrors(errors ?? [], validationErrors ?? [], false, reorderedRows);
};

const onDeleteClick = async rowsToDelete => {
    const result = await AiCustomConfirmDialog('Строки будут безвозвратно удалены. Продолжить?', 'Запрос на удаление');

    if (result === -1 || result === 0) {
        // treeListInstance.value.clearSelection();
        return;
    }

    treeListInstance.value.clearSelection();

    const { errors, validationErrors } = await commitDataChanges(props.meta.api.save, rowsToDelete, args.value.save);

    await processErrors(errors ?? [], validationErrors ?? [], true);
};

const processErrors = async (errors, validationErrors, deletingCase) => {
    if (errors.length === 0 && validationErrors.length === 0) {
        if (!deletingCase) {
            treeListInstance.value.option('editing.changes', []);
        }

        treeListRef.value.setReorderedRows([]);

        await setDataSource();
        dxUiNotify('Изменения данных прошли успешно', 'success');
        emit('refreshEnd');
        emit('changesSavedSuccessfully');
        return;
    }

    if (validationErrors.length > 0) {
        validationErrorsList.value = [...validationErrors];

        validationErrorsList.value.forEach(error => {
            tooltipConfigList.value.push({
                key: error.item.greyEminence,
                field: error.field,
                message: error.message.join(';'),
            });
        });

        dxUiNotify('Ошибка сохранения данных', 'error');
        setTimeout(() => {
            highlightErrors();
        }, 500);
        return;
    }

    if (errors.length > 0) {
        const errorMsgs = [];

        errors.forEach(error => {
            if (!errorMsgs.includes(error.message)) {
                systemErrorIds.value.push(error.node.greyEminence);
                errorMsgs.push(error.message);
            }
        });

        setFocus(-1);
        const message = errorMsgs.join('\n');
        dxUiNotify(message, 'error');
        emit('refreshEnd');
    }
};

const setUserDataChanges = (userData, isReady) => {
    treeListInstance.value.option('editing.changes', userData);
    setTabReadyToSaveValue(false);
    if (isReady) {
        setTabReadyToSaveValue(true);
    }
};

const onCellValueChanged = async (userData, event) => {
    if (!isUserDataLoaded.value) return;
    const args = {
        viewId: props.meta.viewId,
        instanceId: null,
        changes: userData,
    };

    if (event) {
        const found = event.value.some(item => {
            if (Object.keys(item.data).length === 0) return false;

            const field = Object.keys(item.data)[0];
            return tooltipConfigList.value.find(error => {
                return error.key === item.key && error.field === field;
            });
        });

        if (found) {
            validationErrorsList.value = validationErrorsList.value.filter(({ item: { greyEminence }, field }) => {
                return !event.value.some(e => e.key === greyEminence && Object.keys(e.data)[0] === field);
            });
        }
    }

    await saveUserDataChanges(args);
};

const onUserSettingsChanged = async (userSettingsData, autoSaveMode) => {
    if (!isUserDataLoaded.value) return;
    const args = {
        viewId: props.meta.viewId,
        settings: userSettingsData,
    };

    emit('userSettingsChanged', args, autoSaveMode);
    state.userSettings = userSettingsData;
    setPrefState(userSettingsData);
};

const onUserSettingsReset = async () => {
    treeListInstance.value.state({});
    state.userSettings = {};
    state.prefState = true;
    await deleteUserSettingsChanges({
        viewId: props.meta.viewId,
        settings: {},
    });
};

const onRefreshClick = async (treeListChanges, processedData, fromParent = false) => {
    if (treeListChanges.length > 0) {
        const result = await AiCustomConfirmDialog('Сохранить изменения?', 'Запрос на сохранение');

        if (result === 1) {
            await onSaveClick(processedData);
            return;
        }
        if (result === -1) return;
    }

    isUserDataLoaded.value = false;
    treeListInstance.value.option('editing.changes', []);
    treeListInstance.value.clearSelection();
    isUserDataLoaded.value = true;

    systemErrorIds.value = [];
    validationErrorsList.value = [];

    const { result, changes } = await getUserDataChanges({ viewId: props.meta.viewId });
    if (result && changes) await deleteUserDataChanges({ viewId: props.meta.viewId });

    treeListRef.value.setReorderedRows([]);
    treeListRef.value.setSearchPanelValue(null);

    await fetchAndSetUserData({ viewId: props.meta.viewId });
    await setDataSource();

    if (!fromParent) {
        setFocus(0);
        dxUiNotify('Данные обновлены', 'success');
    }
    emit('refreshEnd');
};

const onDragChange = async event => {
    emit('dragChange', event);
};

const onDragReorder = async event => {
    emit('dragReorder', event);
    if (props.meta.autoSaveDragDrop) {
        await autoSave();
    }
};

const onCutPutEnd = async event => {
    emit('cutPutEnd', event);
    if (props.meta.autoSaveCutPut) {
        await autoSave();
    }
};

const autoSave = async () => {
    treeListInstance.value.option('disabled', true);
    treeListInstance.value.beginCustomLoading('Сохранение данных');
    await onSaveClick(await treeListRef.value.prepareChangesForSaving());
    treeListInstance.value.option('disabled', false);
    treeListInstance.value.endCustomLoading();
};

const setFocus = index => {
    setTimeout(() => {
        if (index < 0) {
            treeListInstance.value.option('focusedColumnIndex', -1);
            treeListInstance.value.option('focusedRowIndex', -1);
            treeListInstance.value.option('focusedRowKey', null);
            return;
        }
        const key = treeListInstance.value.getKeyByRowIndex(index);

        treeListInstance.value.option('focusedColumnIndex', 0);
        treeListInstance.value.option('focusedRowIndex', index);
        treeListInstance.value.option('focusedRowKey', key);
    }, 300);
};

const checkBeforeClose = async (confirmMsg = 'Сохранить изменения?') => {
    const processedData = await treeListRef.value.prepareChangesForSaving();
    const userData = treeListRef.value.userData;
    if (userData?.treeListChanges.length > 0) {
        const result = await AiCustomConfirmDialog(confirmMsg, 'Запрос на сохранение');

        if (result === 1) {
            await onSaveClick(processedData);
            return validationErrorsList.value.length === 0;
        }
        if (result === -1) return false;
    }

    isUserDataLoaded.value = false;
    treeListInstance.value.option('editing.changes', []);
    treeListInstance.value.clearSelection();
    isUserDataLoaded.value = true;

    systemErrorIds.value = [];
    validationErrorsList.value = [];
    return true;
};

const toolbarConfig = {
    order: [
        {
            name: 'refreshButton',
        },
        {
            name: 'expandOrCollapseButton',
        },
        {
            name: 'copyButton',
        },
        {
            name: 'addRootRowButton',
        },
        {
            name: 'addChildRowButton',
        },
        {
            name: 'deleteButton',
        },
        {
            name: 'saveButton',
        },
    ],
    excluded: [],
    customTools: [],
};

defineExpose({
    treeListInstance,
    setDataSource,
    expandAll: () => treeListRef.value.expandAll(),
    collapseAll: () => treeListRef.value.collapseAll(),
    setSelected: () => treeListRef.value.setSelected(),
    saveUserDataChanges,
    getTreeListDataSource: (ms = 500) => treeListRef.value.getTreeListDataSource(ms),
    refreshSaveButtonDisable: () => treeListRef.value.refreshSaveButtonDisable(),
    prepareChangesForSaving: () => treeListRef.value.prepareChangesForSaving(),
    saveChanges: userChanges => onSaveClick(userChanges),
    setCacheOrApiDataSource,
    setFocusdRow,
    updateChanges: (key, dataField, newValue, originalValue) =>
        treeListRef.value.updateChanges(key, dataField, newValue, originalValue),
    getButtonsRefs: () => treeListRef.value?.getButtonsRefs(),
    checkBeforeClose: checkBeforeClose,
    refreshClick: onRefreshClick,
});
</script>
<style scoped>
:deep(.dx-treelist-rowsview .dx-row > td) {
    overflow: visible;
}

:deep(.dx-editor-cell) {
    overflow: hidden !important;
}

.highlight-cell {
    background-color: #ffff9b;
}
</style>
